import React, { useState } from 'react';

// ========================================================
// 1. Weekly & Monthly Responsive SVG Bar Chart Component
// ========================================================

export interface BarChartDataPoint {
    label: string;
    value: number;
    compareValue?: number; // e.g. last week / last year
    bills?: number;
}

interface BarChartProps {
    data: BarChartDataPoint[];
    title: string;
    subtitle?: string;
    height?: number;
    showComparison?: boolean;
    primaryLabel?: string;
    compareLabel?: string;
}

export const ModernBarChart: React.FC<BarChartProps> = ({
    data,
    title,
    subtitle,
    height = 260,
    showComparison = false,
    primaryLabel = 'ช่วงนี้',
    compareLabel = 'ช่วงก่อนหน้า'
}) => {
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    const maxValue = Math.max(...data.map(d => Math.max(d.value, d.compareValue || 0)), 100);
    const chartHeight = height - 60; // leave room for labels

    return (
        <div className="analytics-chart-card">
            <div className="chart-card-header">
                <div>
                    <div className="chart-card-title">{title}</div>
                    {subtitle && <div className="chart-card-subtitle">{subtitle}</div>}
                </div>
                {showComparison && (
                    <div className="chart-legend-box">
                        <div className="legend-item">
                            <span className="legend-dot primary"></span>
                            <span>{primaryLabel}</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot compare"></span>
                            <span>{compareLabel}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* SVG Bar Chart */}
            <div className="chart-svg-wrapper" style={{ height: `${height}px` }}>
                <svg width="100%" height="100%" viewBox={`0 0 ${data.length * 60 + 40} ${height}`} preserveAspectRatio="none">
                    <defs>
                        <linearGradient id="barGradientPrimary" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#FFA07A" />
                            <stop offset="100%" stopColor="#FF6B4A" />
                        </linearGradient>
                        <linearGradient id="barGradientCompare" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#CBD5E1" />
                            <stop offset="100%" stopColor="#94A3B8" />
                        </linearGradient>
                    </defs>

                    {/* Horizontal Grid lines */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
                        const y = chartHeight * (1 - ratio) + 20;
                        return (
                            <g key={i}>
                                <line x1="30" y1={y} x2={data.length * 60 + 20} y2={y} stroke="var(--border-color, #E2E8F0)" strokeDasharray="3 3" opacity="0.7" />
                                <text x="5" y={y + 4} fontSize="9.5" fill="var(--text-secondary, #94A3B8)" textAnchor="start">
                                    ฿{Math.round((maxValue * ratio) / 1000)}k
                                </text>
                            </g>
                        );
                    })}

                    {/* Bars */}
                    {data.map((item, idx) => {
                        const xBase = 45 + idx * 60;
                        const barWidth = showComparison ? 14 : 24;
                        const barHeight1 = Math.max((item.value / maxValue) * chartHeight, 4);
                        const y1 = chartHeight - barHeight1 + 20;

                        const barHeight2 = item.compareValue ? Math.max((item.compareValue / maxValue) * chartHeight, 4) : 0;
                        const y2 = chartHeight - barHeight2 + 20;

                        const isHovered = hoveredIdx === idx;

                        return (
                            <g
                                key={idx}
                                onMouseEnter={() => setHoveredIdx(idx)}
                                onMouseLeave={() => setHoveredIdx(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {/* Comparison Bar (if enabled) */}
                                {showComparison && item.compareValue !== undefined && (
                                    <rect
                                        x={xBase - 8}
                                        y={y2}
                                        width={barWidth}
                                        height={barHeight2}
                                        rx="4"
                                        fill="url(#barGradientCompare)"
                                        opacity={isHovered ? 1 : 0.8}
                                        className="chart-bar-anim"
                                    />
                                )}

                                {/* Primary Bar */}
                                <rect
                                    x={showComparison ? xBase + 8 : xBase - barWidth / 2}
                                    y={y1}
                                    width={barWidth}
                                    height={barHeight1}
                                    rx="5"
                                    fill="url(#barGradientPrimary)"
                                    filter={isHovered ? "drop-shadow(0 4px 8px rgba(255, 107, 74, 0.4))" : undefined}
                                    className="chart-bar-anim"
                                />

                                {/* X-Axis Label */}
                                <text
                                    x={xBase + (showComparison ? 7 : 0)}
                                    y={height - 12}
                                    fontSize="11.5"
                                    fontWeight={isHovered ? "700" : "500"}
                                    fill={isHovered ? "var(--primary, #FF7A59)" : "var(--text-secondary, #64748B)"}
                                    textAnchor="middle"
                                >
                                    {item.label}
                                </text>

                                {/* Value label on top of bar on hover */}
                                {isHovered && (
                                    <g>
                                        <rect
                                            x={xBase - 30}
                                            y={Math.min(y1, y2) - 30}
                                            width="74"
                                            height="24"
                                            rx="6"
                                            fill="#1E293B"
                                        />
                                        <text
                                            x={xBase + 7}
                                            y={Math.min(y1, y2) - 14}
                                            fontSize="11"
                                            fontWeight="700"
                                            fill="#FFFFFF"
                                            textAnchor="middle"
                                        >
                                            ฿{item.value.toLocaleString()}
                                        </text>
                                    </g>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>
        </div>
    );
};

// ========================================================
// 2. Interactive SVG Donut / Pie Chart Component
// ========================================================

export interface DonutChartSegment {
    label: string;
    value: number;
    color: string;
    percentage: number;
    icon?: string;
}

interface DonutChartProps {
    data: DonutChartSegment[];
    title: string;
    subtitle?: string;
    centerLabel?: string;
    centerValue?: string;
    size?: number;
}

export const ModernDonutChart: React.FC<DonutChartProps> = ({
    data,
    title,
    subtitle,
    centerLabel = 'ยอดรวม',
    centerValue,
    size = 180
}) => {
    const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);

    const total = data.reduce((acc, d) => acc + d.value, 0);
    const displayCenterVal = centerValue || `฿${total.toLocaleString()}`;

    // Calculate SVG circle segments
    const radius = 68;
    const circumference = 2 * Math.PI * radius;
    let cumulativePercent = 0;

    return (
        <div className="analytics-chart-card">
            <div className="chart-card-header">
                <div>
                    <div className="chart-card-title">{title}</div>
                    {subtitle && <div className="chart-card-subtitle">{subtitle}</div>}
                </div>
            </div>

            <div className="donut-content-row">
                {/* SVG Donut */}
                <div className="donut-svg-wrapper" style={{ width: `${size}px`, height: `${size}px` }}>
                    <svg width={size} height={size} viewBox="0 0 180 180">
                        <circle
                            cx="90"
                            cy="90"
                            r={radius}
                            fill="transparent"
                            stroke="var(--border-color, #F1F5F9)"
                            strokeWidth="24"
                        />
                        {data.map((seg, idx) => {
                            const strokeDasharray = `${(seg.percentage / 100) * circumference} ${circumference}`;
                            const strokeDashoffset = -((cumulativePercent / 100) * circumference);
                            cumulativePercent += seg.percentage;

                            const isHovered = hoveredSegment === idx;

                            return (
                                <circle
                                    key={idx}
                                    cx="90"
                                    cy="90"
                                    r={radius}
                                    fill="transparent"
                                    stroke={seg.color}
                                    strokeWidth={isHovered ? "28" : "24"}
                                    strokeDasharray={strokeDasharray}
                                    strokeDashoffset={strokeDashoffset}
                                    strokeLinecap="round"
                                    transform="rotate(-90 90 90)"
                                    onMouseEnter={() => setHoveredSegment(idx)}
                                    onMouseLeave={() => setHoveredSegment(null)}
                                    style={{
                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                        cursor: 'pointer'
                                    }}
                                />
                            );
                        })}
                    </svg>

                    {/* Donut Center Info */}
                    <div className="donut-center-info">
                        <span className="donut-center-label">
                            {hoveredSegment !== null ? data[hoveredSegment].label : centerLabel}
                        </span>
                        <span className="donut-center-value">
                            {hoveredSegment !== null ? `฿${data[hoveredSegment].value.toLocaleString()}` : displayCenterVal}
                        </span>
                        {hoveredSegment !== null && (
                            <span className="donut-center-percent" style={{ color: data[hoveredSegment].color }}>
                                {data[hoveredSegment].percentage}%
                            </span>
                        )}
                    </div>
                </div>

                {/* Donut Legend */}
                <div className="donut-legend-list">
                    {data.map((seg, idx) => {
                        const isHovered = hoveredSegment === idx;
                        return (
                            <div
                                key={idx}
                                className={`donut-legend-row ${isHovered ? 'active' : ''}`}
                                onMouseEnter={() => setHoveredSegment(idx)}
                                onMouseLeave={() => setHoveredSegment(null)}
                            >
                                <div className="legend-color-tag" style={{ background: seg.color }}></div>
                                <div className="legend-text-col">
                                    <div className="legend-label-text">{seg.label}</div>
                                    <div className="legend-sub-text">฿{seg.value.toLocaleString()} ({seg.percentage}%)</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ========================================================
// 3. Peak Hours Distribution Bar Chart
// ========================================================

export interface HourlySalesPoint {
    hour: string; // e.g. "08:00", "09:00"
    sales: number;
    bills: number;
}

interface PeakHoursChartProps {
    data: HourlySalesPoint[];
}

export const PeakHoursChart: React.FC<PeakHoursChartProps> = ({ data }) => {
    const maxSales = Math.max(...data.map(d => d.sales), 100);

    return (
        <div className="analytics-chart-card">
            <div className="chart-card-header">
                <div>
                    <div className="chart-card-title">🕒 สถิติช่วงเวลาขายดีในแต่ละวัน (Peak Hours)</div>
                    <div className="chart-card-subtitle">วิเคราะห์ยอดขายและจำนวนบิลแยกตามช่วงเวลา เพื่อวางแผนกะพนักงาน</div>
                </div>
            </div>

            <div className="hourly-bars-grid">
                {data.map((item, idx) => {
                    const heightPercent = Math.max(Math.round((item.sales / maxSales) * 100), 8);
                    const isPeak = item.sales === maxSales;

                    return (
                        <div key={idx} className="hourly-bar-col">
                            <div className="hourly-bar-val">฿{item.sales > 0 ? `${Math.round(item.sales / 100) * 100}` : 0}</div>
                            <div className="hourly-bar-track">
                                <div
                                    className={`hourly-bar-fill ${isPeak ? 'peak' : ''}`}
                                    style={{ height: `${heightPercent}%` }}
                                    title={`${item.hour}: ยอดขาย ฿${item.sales.toLocaleString()} (${item.bills} บิล)`}
                                ></div>
                            </div>
                            <div className={`hourly-bar-time ${isPeak ? 'peak-text' : ''}`}>{item.hour}</div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
